import dayjs, { Dayjs } from "dayjs";
import { StudentReviewGroup } from "./useTeacherStudentReviews.ts";
import { STUDENT_SCORE_WINDOW_MONTHS } from "./teacherScoreAlgorithm.ts";

export type StudentReview = {
  userId: string;
  reviewedAt: Dayjs;
  teacherAvg: number;
  clearanceScore: number;
  involvementScore: number;
  comfortableScore: number;
};

export type StudentSubScoreAverages = {
  clearance: number | null;
  involvement: number | null;
  comfortable: number | null;
};

export type StudentReviewsSummary = {
  average: number | null;
  subScoreAverages: StudentSubScoreAverages;
  reviewCount: number;
  studentCount: number;
};

export type ScoreWindowComparison = {
  scoreWindow: StudentReviewsSummary;
  previousWindow: StudentReviewsSummary;
};

export function flattenStudentReviews(
  studentReviewGroups: StudentReviewGroup[],
): StudentReview[] {
  return studentReviewGroups.flatMap((group) =>
    group.reviews.map((review) => ({
      userId: group.userId,
      reviewedAt: dayjs(review.reviewedAtUtc),
      teacherAvg: review.teacherAvg,
      clearanceScore: review.clearanceScore,
      involvementScore: review.involvementScore,
      comfortableScore: review.comfortableScore,
    })),
  );
}

function averageOf(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function summarizeStudentReviews(
  reviews: StudentReview[],
): StudentReviewsSummary {
  return {
    average: averageOf(reviews.map((review) => review.teacherAvg)),
    subScoreAverages: {
      clearance: averageOf(reviews.map((review) => review.clearanceScore)),
      involvement: averageOf(reviews.map((review) => review.involvementScore)),
      comfortable: averageOf(reviews.map((review) => review.comfortableScore)),
    },
    reviewCount: reviews.length,
    studentCount: new Set(reviews.map((review) => review.userId)).size,
  };
}

export function compareScoreWindowWithPrevious({
  studentReviewGroups,
  now,
}: {
  studentReviewGroups: StudentReviewGroup[];
  now: Dayjs;
}): ScoreWindowComparison {
  const studentReviews = flattenStudentReviews(studentReviewGroups);
  const scoreWindowStart = now.subtract(STUDENT_SCORE_WINDOW_MONTHS, "month");
  const previousWindowStart = scoreWindowStart.subtract(
    STUDENT_SCORE_WINDOW_MONTHS,
    "month",
  );

  return {
    scoreWindow: summarizeStudentReviews(
      studentReviews.filter(
        (review) => !review.reviewedAt.isBefore(scoreWindowStart),
      ),
    ),
    previousWindow: summarizeStudentReviews(
      studentReviews.filter(
        (review) =>
          !review.reviewedAt.isBefore(previousWindowStart) &&
          review.reviewedAt.isBefore(scoreWindowStart),
      ),
    ),
  };
}
